import { Project } from '@griply/assets';
import { EntityLinks } from '@griply/types';
import { getDirectGoalLifeAreaIds, getGoalParentIds } from '@griply/utils';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DotsMenu12, Lock12 } from 'shared/assets/icons';
import {
  Button,
  Colors,
  Sizes as ButtonSizes,
  Variants,
} from 'shared/components/ui/button';
import * as SharedStyled from 'shared/components/ui/goal-insights-card/goal-insights-card.style';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors as IconButtonColors,
  IconButton,
  Sizes,
} from 'shared/components/ui/icon-button';
import { Goal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';

import * as Styled from './projects-insights-card.style';

export type ProjectsInsightsHeaderProps = {
  goal: Goal;
  goals: Goal[];
  lifeAreas: LifeArea[];
  links?: EntityLinks;
  menuButtonRef?: React.RefObject<HTMLButtonElement>;
  onMenuButton?: (e: React.MouseEvent) => void;
  onEditGoalProgress?: (e: React.MouseEvent) => void;
};

export const ProjectsInsightsHeader: React.FC<ProjectsInsightsHeaderProps> = ({
  goal,
  goals,
  lifeAreas,
  links,
  menuButtonRef,
  onMenuButton,
  onEditGoalProgress,
}) => {
  const { t } = useTranslation();

  const color = useMemo(
    () => getLinkedItemColor(goal.id, { goals, lifeAreas }),
    [goal.id, goals, lifeAreas],
  );

  // The project's direct parent — a goal (when linked to one) or its life area.
  const parentName = useMemo(() => {
    const [parentGoalId] = getGoalParentIds(goal, { links });
    if (parentGoalId) {
      return goals.find(({ id }) => id === parentGoalId)?.name;
    }
    const [lifeAreaId] = getDirectGoalLifeAreaIds(goal, links);
    if (lifeAreaId) {
      return lifeAreas.find(({ id }) => id === lifeAreaId)?.name;
    }
    return undefined;
  }, [goal, goals, lifeAreas, links]);

  const _onMenuButton = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onMenuButton?.(e);
  };

  const _onUpdate = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onEditGoalProgress?.(e);
  };

  return (
    <SharedStyled.Header>
      <SharedStyled.GoalInformationContainer>
        <SharedStyled.GoalInformationMain>
          <Styled.ProjectIcon $color={color ?? undefined}>
            <Icon icon={Project} />
          </Styled.ProjectIcon>

          <Styled.TitleContainer>
            <Styled.TitleRow>
              <SharedStyled.Title
                $isFrozen={!!goal.frozenAt}
                $isCaption={false}
              >
                {goal.name}
              </SharedStyled.Title>

              <SharedStyled.IconButtonContainer>
                <IconButton
                  icon={goal.frozenAt ? Lock12 : DotsMenu12}
                  onClick={goal.frozenAt ? undefined : _onMenuButton}
                  size={Sizes.ExtraSmall}
                  color={IconButtonColors.Default}
                  ref={menuButtonRef}
                />
              </SharedStyled.IconButtonContainer>
            </Styled.TitleRow>

            {!!parentName && (
              <Styled.ParentName>{parentName}</Styled.ParentName>
            )}
          </Styled.TitleContainer>
        </SharedStyled.GoalInformationMain>
      </SharedStyled.GoalInformationContainer>

      <SharedStyled.Interactions>
        {!!onEditGoalProgress && (
          <Button
            onClick={_onUpdate}
            variant={Variants.Outlined}
            size={ButtonSizes.Small}
            color={Colors.Inherit}
          >
            {t('general.labels.insights-card.update-target.short.label')}
          </Button>
        )}
      </SharedStyled.Interactions>
    </SharedStyled.Header>
  );
};
